import React, { useEffect, useState } from "react";
import { useUser, SignInButton } from "@clerk/clerk-react";
import { ArrowRight, ChevronDown } from "lucide-react";
import {
  createCheckoutSession,
  createBillingPortalSession,
  fetchUserByEmail,
  fetchCards,
} from "../utils/api";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Display,
  Hairline,
  SEO,
} from "./ui";
import { canonicalFor } from "../routes";

const PRICE_PRO_MONTHLY    = process.env.REACT_APP_PRICE_PRO_MONTHLY    || "";
const PRICE_PRO_YEARLY     = process.env.REACT_APP_PRICE_PRO_YEARLY     || "";
const PRICE_PHYSICAL_CARD  = process.env.REACT_APP_PRICE_PHYSICAL_CARD  || "";

interface Tier {
  name: string;
  price: string;
  suffix: string;
  yearlyPrice?: string;
  yearlyNote?: string;
  description: string;
  features: string[];
  cta: string;
  ctaPath?: string;
}

const TIERS: Tier[] = [
  {
    name: "Starter",
    price: "Free",
    suffix: "",
    description: "Everything you need to start.",
    features: [
      "One virtual card",
      "Up to five links",
      "Six themes",
      "Public profile page",
      "QR code",
      "Basic insights (total views)",
      "WhoBrew branding on page",
    ],
    cta: "Get started",
    ctaPath: "/my-card",
  },
  {
    name: "Pro",
    price: "$4.99",
    suffix: "/month",
    yearlyPrice: "$3.99",
    yearlyNote: "billed annually, save 20%",
    description: "For people who network seriously.",
    features: [
      "Everything in Starter",
      "Up to ten links",
      "Up to three cards",
      "All eleven themes",
      "Remove WhoBrew branding",
      "Click-tracking insights",
      "Timeline chart and CSV export",
      "Custom QR colors",
      "Pro badge",
      "Priority support",
    ],
    cta: "Upgrade to Pro",
  },
  {
    name: "NFC card",
    price: "$14.99",
    suffix: " once",
    description: "Tap to share. No app needed.",
    features: [
      "Premium PVC card",
      "Embedded NFC chip",
      "Printed QR code on back",
      "Black card, white text",
      "Logo and field on card",
      "Free shipping in the continental US",
      "Batch ordering for teams",
    ],
    cta: "Order your card",
  },
];

const PRICING_FAQ = [
  {
    q: "Can I cancel my Pro subscription anytime?",
    a: "Yes. Cancel from your account settings. You keep Pro access until the end of the current billing period. No fees, no questions.",
  },
  {
    q: "What happens if I downgrade to Starter?",
    a: "Your card and existing links stay live. You just can't add new links beyond the Starter limit until you re-upgrade.",
  },
  {
    q: "Is the physical NFC card a one-time charge?",
    a: "Yes. $14.99, one time. You own the card. There is no subscription tied to the physical card and no expiry on the chip itself.",
  },
  {
    q: "Do I need a virtual card before ordering the physical one?",
    a: "Yes. The NFC card is printed with your @handle, so the handle has to exist before we can press it. Setting up the virtual card takes about a minute.",
  },
  {
    q: "Is there a free trial for Pro?",
    a: "Starter is free forever and covers the basics. You can upgrade to Pro at any time and cancel within the first billing cycle if it isn't right for you.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: PRICING_FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const PRODUCT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "WhoBrew",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  offers: [
    { "@type": "Offer", name: "Starter",  price: "0",     priceCurrency: "USD" },
    { "@type": "Offer", name: "Pro",      price: "4.99",  priceCurrency: "USD", availability: "https://schema.org/InStock" },
    { "@type": "Offer", name: "NFC card", price: "14.99", priceCurrency: "USD" },
  ],
};

function Pricing() {
  const { user, isSignedIn } = useUser();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [loadingCheckout, setLoadingCheckout] = useState<string | null>(null);
  const [userSubscription, setUserSubscription] = useState<string>("free");
  const [hasCard, setHasCard] = useState<boolean | null>(null);
  const [userHandle, setUserHandle] = useState<string>("");
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  useEffect(() => {
    if (user?.primaryEmailAddress?.emailAddress) {
      fetchUserByEmail(user.primaryEmailAddress.emailAddress)
        .then(async (u) => {
          setUserSubscription(u.subscription || "free");
          try {
            const cards = await fetchCards(u.id);
            setHasCard(cards.length > 0);
            if (cards.length > 0) setUserHandle(cards[0].username);
          } catch {
            setHasCard(false);
          }
        })
        .catch(() => setHasCard(false));
    } else {
      setHasCard(false);
    }
  }, [user]);

  const isYearly = billingCycle === "yearly";

  const handleManageSubscription = async () => {
    if (!isSignedIn || !user?.primaryEmailAddress?.emailAddress) return;
    try {
      const { url } = await createBillingPortalSession(user.primaryEmailAddress.emailAddress);
      if (url) window.location.href = url;
    } catch {
      /* silent */
    }
  };

  const handleCheckout = async (
    priceId: string,
    opts: { skipHandleConfirm?: boolean } = {}
  ) => {
    if (!isSignedIn || !user?.primaryEmailAddress?.emailAddress) {
      setLoadingCheckout("signin");
      return;
    }
    if (priceId === PRICE_PHYSICAL_CARD && hasCard === false) {
      setLoadingCheckout("need-card");
      return;
    }
    if (priceId === PRICE_PHYSICAL_CARD && hasCard === true && !opts.skipHandleConfirm) {
      setLoadingCheckout("confirm-handle");
      return;
    }

    setLoadingCheckout(priceId);
    try {
      const email = user.primaryEmailAddress.emailAddress;
      let userId = "";
      try {
        const dbUser = await fetchUserByEmail(email);
        userId = dbUser.id;
      } catch {}
      const { url } = await createCheckoutSession(priceId, email, userId);
      if (url) window.location.href = url;
    } catch {
      setLoadingCheckout(null);
    }
  };

  return (
    <>
      <SEO
        title="Pricing — Free forever or $4.99/month Pro — WhoBrew"
        description="Three plans. Starter is free forever. Pro is $4.99 a month with no annual contract. NFC cards are $14.99 once. Honest pricing, no hidden fees."
        canonical={canonicalFor("/pricing")}
        jsonLd={[PRODUCT_JSONLD, FAQ_JSONLD]}
      />

      {/* Modals */}
      {loadingCheckout === "signin" && (
        <Modal onClose={() => setLoadingCheckout(null)} title="Sign in to continue" body="You need an account to buy a plan.">
          <SignInButton mode="modal">
            <Button fullWidth onClick={() => setLoadingCheckout(null)}>Sign in</Button>
          </SignInButton>
        </Modal>
      )}
      {loadingCheckout === "need-card" && (
        <Modal onClose={() => setLoadingCheckout(null)} title="Create your virtual card first" body="Your NFC card gets printed with your @handle, so you need one set up before we can print. Takes about a minute.">
          <Button to="/my-card" fullWidth onClick={() => setLoadingCheckout(null)}>Create my card</Button>
        </Modal>
      )}
      {loadingCheckout === "confirm-handle" && (
        <Modal
          onClose={() => setLoadingCheckout(null)}
          title="Confirm your handle"
          body={
            <>
              <p className="mb-4">Your NFC card will be printed to link to:</p>
              <p className="font-display text-3xl text-coffee-900 dark:text-cream-50 mb-4">@{userHandle}</p>
              <p className="text-caption text-warning-700 bg-warning-50 px-4 py-3 mb-6">
                You can only change your handle <strong>once</strong>. If you want a different one, change it on your card page <em>before</em> ordering.
              </p>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <Button to="/my-card" variant="secondary" onClick={() => setLoadingCheckout(null)}>Change handle</Button>
            <Button onClick={() => { setLoadingCheckout(null); handleCheckout(PRICE_PHYSICAL_CARD, { skipHandleConfirm: true }); }}>
              Order card
            </Button>
          </div>
        </Modal>
      )}

      {/* Hero */}
      <Section tone="page" spacing="loose" hairlineBottom>
        <Container width="prose" className="text-center">
          <Eyebrow>Pricing</Eyebrow>
          <Display as="h1" scale="lg" italicWord="honest" className="mt-5">
            Honest pricing. Read it in one breath.
          </Display>
          <p className="mt-7 text-lg text-coffee-700 dark:text-cream-200">
            Free is free, forever. Pro is $4.99 a month with no annual contract. The NFC card
            is $14.99, once.
          </p>

          {/* Billing toggle */}
          <div className="mt-10 inline-flex border border-latte-300 dark:border-latte-800 rounded-DEFAULT overflow-hidden">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-3 text-body-sm font-medium transition-colors ${
                billingCycle === "monthly"
                  ? "bg-coffee-900 text-cream-50 dark:bg-cream-50 dark:text-coffee-900"
                  : "text-coffee-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-coffee-800"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-6 py-3 text-body-sm font-medium transition-colors flex items-center gap-2 ${
                billingCycle === "yearly"
                  ? "bg-coffee-900 text-cream-50 dark:bg-cream-50 dark:text-coffee-900"
                  : "text-coffee-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-coffee-800"
              }`}
            >
              Yearly
              <span className="font-mono text-eyebrow uppercase text-accent-500">save 20%</span>
            </button>
          </div>
        </Container>
      </Section>

      {/* Tiers */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-latte-300 dark:bg-latte-800 border border-latte-300 dark:border-latte-800 rounded-lg overflow-hidden">
            {TIERS.map((tier) => (
              <div
                key={tier.name}
                className="flex flex-col bg-cream-50 dark:bg-coffee-900 p-8 sm:p-10"
              >
                <Eyebrow>{tier.name}</Eyebrow>
                <p className="mt-3 text-body-sm text-coffee-700 dark:text-cream-200">{tier.description}</p>

                <div className="mt-6">
                  <p className="font-display text-display-md text-coffee-900 dark:text-cream-50 tracking-tight tabular-nums">
                    {tier.yearlyPrice && isYearly ? tier.yearlyPrice : tier.price}
                    {tier.suffix && (
                      <span className="text-headline text-coffee-700 dark:text-cream-200">
                        {tier.suffix}
                      </span>
                    )}
                  </p>
                  {tier.yearlyPrice && isYearly && tier.yearlyNote && (
                    <p className="mt-1 font-mono text-caption text-coffee-600 dark:text-cream-200">
                      {tier.yearlyNote}
                    </p>
                  )}
                </div>

                <Hairline className="my-7" />

                <ul className="flex-1 space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex gap-3 text-body-sm text-coffee-700 dark:text-cream-200"
                    >
                      <span className="text-accent-500 font-mono shrink-0">·</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {tier.name === "Starter" ? (
                  <Button to={tier.ctaPath!} fullWidth size="lg">{tier.cta}</Button>
                ) : tier.name === "Pro" && (userSubscription === "pro" || userSubscription === "paid") ? (
                  <Button onClick={handleManageSubscription} fullWidth size="lg">Manage subscription</Button>
                ) : (
                  <Button
                    onClick={() =>
                      tier.name === "Pro"
                        ? handleCheckout(isYearly ? PRICE_PRO_YEARLY : PRICE_PRO_MONTHLY)
                        : handleCheckout(PRICE_PHYSICAL_CARD)
                    }
                    disabled={loadingCheckout !== null}
                    fullWidth
                    size="lg"
                    loading={
                      !!loadingCheckout &&
                      ((tier.name === "Pro" && (loadingCheckout === PRICE_PRO_MONTHLY || loadingCheckout === PRICE_PRO_YEARLY)) ||
                        (tier.name === "NFC card" && loadingCheckout === PRICE_PHYSICAL_CARD))
                    }
                  >
                    {tier.cta}
                  </Button>
                )}
                {tier.name === "Pro" && (
                  <p className="mt-3 font-mono text-caption text-coffee-600 dark:text-cream-200 text-center">
                    Cancel anytime. No contracts.
                  </p>
                )}
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Teams */}
      <Section tone="raised" spacing="tight" hairlineBottom>
        <Container width="prose" className="text-center">
          <Eyebrow>Teams</Eyebrow>
          <Display as="h2" scale="md" italicWord="team" className="mt-3">
            Cards for an entire team.
          </Display>
          <p className="mt-5 text-body text-coffee-700 dark:text-cream-200">
            Centralized branding, centralized billing, member management, performance roll-ups.
            $3.99 per person per month, no annual contract.
          </p>
          <div className="mt-8">
            <Button to="/teams" size="lg">See team plans</Button>
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Pricing questions</Eyebrow>
          <Display as="h2" scale="md" italicWord="ask" className="mt-4 mb-12">
            What people ask before they buy.
          </Display>
          <div className="divide-y divide-latte-300 dark:divide-latte-800 border-y border-latte-300 dark:border-latte-800">
            {PRICING_FAQ.map((item, i) => {
              const open = openFAQ === i;
              return (
                <div key={i}>
                  <button
                    onClick={() => setOpenFAQ(open ? null : i)}
                    aria-expanded={open}
                    className="w-full text-left py-6 flex items-start justify-between gap-6"
                  >
                    <span className="font-display text-headline text-coffee-900 dark:text-cream-50 leading-snug">
                      {item.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 mt-2 text-coffee-700 dark:text-cream-200 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  {open && (
                    <div className="pb-8 text-body text-coffee-700 dark:text-cream-200 leading-relaxed">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section tone="ink" spacing="default">
        <Container className="text-center">
          <Display as="h2" scale="lg" italicWord="minute" className="mx-auto max-w-3xl">
            Set up in under a minute.
          </Display>
          <div className="mt-10 flex justify-center">
            <Button to="/my-card" size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
              Get started free
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
}

function Modal({
  onClose,
  title,
  body,
  children,
}: {
  onClose: () => void;
  title: string;
  body: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-coffee-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-cream-50 dark:bg-coffee-800 border border-latte-300 dark:border-latte-700 rounded-lg p-8 max-w-md w-full">
        <Display as="h3" scale="sm" className="mb-3">{title}</Display>
        <div className="text-body-sm text-coffee-700 dark:text-cream-200 mb-6">{body}</div>
        {children}
        <button
          onClick={onClose}
          className="mt-4 w-full text-body-sm text-coffee-600 dark:text-cream-200 hover:text-coffee-900 dark:hover:text-cream-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default Pricing;
